import { useRef } from 'react'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import {
  ApprovalPolicy,
  ApprovalWriteBody,
  ApprovalWriteResponse,
  DocumentApprovalState,
  PolicyApplicationBody,
  PolicyApplicationResponse,
  RejectionWriteBody,
  UpdatePolicyVersionBody,
  UpdatePolicyVersionResponse,
} from '@/types/approvals'
import {
  attachPolicyToState,
  detachPolicyFromState,
} from '@/utils/approval-state'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import queryKeys from '../query-keys'
import { createDetail, getDetail } from '../request-wrappers'

/**
 * MARK: Action Hook: Get Document Approval State
 * Server-derived approval progress for one document, from pd-approval.
 * Satisfaction, rejection and turn order are all computed by the service —
 * render the payload as-is.
 *
 * Cached under the document's own key so invalidating a document sweeps its
 * approval state too, even though the data comes from a different service.
 */
export function useGetDocumentApprovalState({
  id, // This is the document ID
  ...options
}: UseGetDetailOptions<DocumentApprovalState>) {
  const query = useGetDetail<DocumentApprovalState>({
    id,
    queryKey: queryKeys.documents.detail({ id, filters: options.filters }, [
      'approval_state',
    ]),
    serviceFn: (serviceFnOptions) => {
      return getDetail({
        url: `/v2/pd-approval/documents/${id}/approval_state`,
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    approvalState: query.data,
    ...query,
  }
}

type UseApproveDocumentOptions = UseCreateDetailOptions<
  ApprovalWriteBody,
  ApprovalWriteResponse
> & {
  documentId: string
  approvalStateQueryKey?: QueryKey
}

/**
 * MARK: Action Hook: Approve Document
 * Records the current user's approval against one policy — the acting user
 * comes from the auth token, never the request body. The response carries the
 * full post-write approval state, which is written straight into
 * `approvalStateQueryKey`: no follow-up fetch, no merge. Pass the key from
 * `useGetDocumentApprovalState`.
 */
export function useApproveDocument({
  documentId,
  approvalStateQueryKey,
  onSuccess,
  onMutate,
  ...options
}: UseApproveDocumentOptions) {
  const queryClient = useQueryClient()

  const mutation = useCreateDetail<ApprovalWriteBody, ApprovalWriteResponse>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: `/v2/pd-approval/documents/${documentId}/approve`,
        ...serviceFnOptions,
      })
    },
    onMutate: async (item, mutationFnContext) => {
      // Cancel first so an in-flight GET of this key can't resolve after the
      // write and overwrite its response. Same as useUpdateDetail.
      if (approvalStateQueryKey) {
        await queryClient.cancelQueries({ queryKey: approvalStateQueryKey })
      }
      return onMutate?.(item, mutationFnContext)
    },
    onSuccess: (data, variables, onMutateResult, mutationFnContext) => {
      if (approvalStateQueryKey) {
        queryClient.setQueryData(approvalStateQueryKey, data.approval_state)
      }
      onSuccess?.(data, variables, onMutateResult, mutationFnContext)
    },
    ...options,
  })

  return {
    approveDocument: mutation.mutate,
    approveDocumentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

type UseRejectDocumentOptions = UseCreateDetailOptions<
  RejectionWriteBody,
  ApprovalWriteResponse
> & {
  documentId: string
  approvalStateQueryKey?: QueryKey
}

/**
 * MARK: Action Hook: Reject Document
 * Same contract as approval, plus an optional reason. Re-rejecting or
 * reversing a prior decision is a normal write here — there's no
 * duplicate-action error to special-case.
 */
export function useRejectDocument({
  documentId,
  approvalStateQueryKey,
  onSuccess,
  onMutate,
  ...options
}: UseRejectDocumentOptions) {
  const queryClient = useQueryClient()

  const mutation = useCreateDetail<RejectionWriteBody, ApprovalWriteResponse>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: `/v2/pd-approval/documents/${documentId}/reject`,
        ...serviceFnOptions,
      })
    },
    onMutate: async (item, mutationFnContext) => {
      // Cancel first so an in-flight GET of this key can't resolve after the
      // write and overwrite its response.
      if (approvalStateQueryKey) {
        await queryClient.cancelQueries({ queryKey: approvalStateQueryKey })
      }
      return onMutate?.(item, mutationFnContext)
    },
    onSuccess: (data, variables, onMutateResult, mutationFnContext) => {
      if (approvalStateQueryKey) {
        queryClient.setQueryData(approvalStateQueryKey, data.approval_state)
      }
      onSuccess?.(data, variables, onMutateResult, mutationFnContext)
    },
    ...options,
  })

  return {
    rejectDocument: mutation.mutate,
    rejectDocumentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

type UsePolicyApplicationOptions = UseCreateDetailOptions<
  PolicyApplicationBody,
  PolicyApplicationResponse
> & {
  documentId: string
  approvalStateQueryKey?: QueryKey
  /**
   * Attaching a policy optimistically needs its config. Without these, an
   * attach only shows once the write comes back.
   */
  projectPolicies?: ApprovalPolicy[]
}

/** Apply and remove differ only in the URL's last segment. */
function useApplyOrRemoveApprovalPolicy(
  action: 'attach-policy' | 'detach-policy',
  {
    documentId,
    approvalStateQueryKey,
    projectPolicies,
    onSuccess,
    onMutate,
    onError,
    ...options
  }: UsePolicyApplicationOptions,
) {
  const queryClient = useQueryClient()

  // useCreateDetail drops what our onMutate returns, so the pre-write state is
  // stashed here rather than in the mutation context.
  const previousState = useRef<DocumentApprovalState | undefined>(undefined)

  return useCreateDetail<PolicyApplicationBody, PolicyApplicationResponse>({
    serviceFn: ({ item, filters, axiosOptions }) => {
      return createDetail({
        url: `/v2/pd-approval/documents/${documentId}/${action}`,
        item,
        filters,
        axiosOptions,
      })
    },
    onMutate: async (item, mutationFnContext) => {
      previousState.current = undefined

      if (approvalStateQueryKey) {
        // Cancel first, for two reasons: an in-flight GET of this key must not
        // resolve after the write and overwrite its response, and cancelling
        // undoes the patch below if it happens after it.
        await queryClient.cancelQueries({ queryKey: approvalStateQueryKey })

        const current = queryClient.getQueryData<DocumentApprovalState>(
          approvalStateQueryKey,
        )
        const patched =
          current &&
          (action === 'attach-policy'
            ? attachPolicyToState(
                current,
                projectPolicies?.find(
                  (policy) => policy.id === item.approval_policy_id,
                ),
              )
            : detachPolicyFromState(current, item.approval_policy_id))

        // The helpers hand back the same object when there was nothing to
        // change — and so nothing to roll back.
        if (patched && patched !== current) {
          previousState.current = current
          queryClient.setQueryData(approvalStateQueryKey, patched)
        }
      }
      return onMutate?.(item, mutationFnContext)
    },
    onError: (error, item, onMutateResult, mutationFnContext) => {
      if (approvalStateQueryKey && previousState.current) {
        queryClient.setQueryData(approvalStateQueryKey, previousState.current)
      }
      onError?.(error, item, onMutateResult, mutationFnContext)
    },
    onSuccess: (data, variables, onMutateResult, mutationFnContext) => {
      if (approvalStateQueryKey) {
        queryClient.setQueryData(approvalStateQueryKey, data.approval_state)
      }
      onSuccess?.(data, variables, onMutateResult, mutationFnContext)
    },
    ...options,
  })
}

/** MARK: Action Hook: Apply Approval Policy */
export function useApplyApprovalPolicy(options: UsePolicyApplicationOptions) {
  const mutation = useApplyOrRemoveApprovalPolicy('attach-policy', options)

  return {
    applyApprovalPolicy: mutation.mutate,
    applyApprovalPolicyAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/** MARK: Action Hook: Remove Approval Policy */
export function useRemoveApprovalPolicy(options: UsePolicyApplicationOptions) {
  const mutation = useApplyOrRemoveApprovalPolicy('detach-policy', options)

  return {
    removeApprovalPolicy: mutation.mutate,
    removeApprovalPolicyAsync: mutation.mutateAsync,
    ...mutation,
  }
}

type UseUpdatePolicyToLatestOptions = UseCreateDetailOptions<
  UpdatePolicyVersionBody,
  UpdatePolicyVersionResponse
> & {
  documentId: string
  approvalPolicyId: string
  approvalStateQueryKey?: QueryKey
}

/**
 * MARK: Action Hook: Update Policy To Latest
 * Gated server-side on the policy-edit permission — callers without it get a
 * 403. A 200 carries the new approval state, written straight into the cache;
 * a 202 means nothing is decided yet, so the cached state is invalidated
 * instead.
 */
export function useUpdatePolicyToLatest({
  documentId,
  approvalPolicyId,
  approvalStateQueryKey,
  onSuccess,
  onMutate,
  ...options
}: UseUpdatePolicyToLatestOptions) {
  const queryClient = useQueryClient()

  const mutation = useCreateDetail<
    UpdatePolicyVersionBody,
    UpdatePolicyVersionResponse
  >({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: `/v2/pd-approval/documents/${documentId}/policies/${approvalPolicyId}/update-to-latest`,
        ...serviceFnOptions,
      })
    },
    onMutate: async (item, mutationFnContext) => {
      // See useApproveDocument's onMutate.
      if (approvalStateQueryKey) {
        await queryClient.cancelQueries({ queryKey: approvalStateQueryKey })
      }
      return onMutate?.(item, mutationFnContext)
    },
    onSuccess: (data, variables, onMutateResult, mutationFnContext) => {
      if (approvalStateQueryKey) {
        if (data.approval_state) {
          queryClient.setQueryData(approvalStateQueryKey, data.approval_state)
        } else {
          queryClient.invalidateQueries({ queryKey: approvalStateQueryKey })
        }
      }
      onSuccess?.(data, variables, onMutateResult, mutationFnContext)
    },
    ...options,
  })

  return {
    updatePolicyToLatest: mutation.mutate,
    updatePolicyToLatestAsync: mutation.mutateAsync,
    ...mutation,
  }
}
